import { isVacuous } from '@yimoka/shared';
import { rootStore } from '@yimoka/store';

const STORAGE_PREFIX = 'tenant-web:auth';
const CLIENT_ID_KEY = 'tenant-web:client-id';
const HYDRA_SESSION_KEY = 'hasHydraSession';
const DEFAULT_CLIENT_ID = 'tenant-web-local';

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function read(key: string): string {
  if (!canUseStorage()) return '';
  return window.localStorage.getItem(key) ?? '';
}

function write(key: string, value?: string | null) {
  if (!canUseStorage()) return;
  if (!value) {
    window.localStorage.removeItem(key);
    return;
  }
  window.localStorage.setItem(key, value);
}

function readJSON<T>(key: string): T | undefined {
  const raw = read(key);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function writeJSON<T>(key: string, value?: T | null) {
  if (!value) {
    write(key);
    return;
  }
  write(key, JSON.stringify(value));
}

function key(name: string) {
  return `${STORAGE_PREFIX}:${name}`;
}

function tenantScopedKey(name: string, tenantID = getTenantID()) {
  return `${STORAGE_PREFIX}:tenant:${tenantID || 'unknown'}:${name}`;
}

export function getClientID() {
  const existing = read(CLIENT_ID_KEY);
  if (existing) return existing;
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : DEFAULT_CLIENT_ID;
  write(CLIENT_ID_KEY, id);
  return id;
}

export function setClientID(clientID?: string | null) {
  write(CLIENT_ID_KEY, clientID);
}

export function getUserToken() {
  const userTokenKey = key('user-token');

  const userTokenValue = rootStore.getDataItem(userTokenKey);
  if (userTokenValue) {
    return userTokenValue;
  }

  const storageUserToken = read(userTokenKey);
  if (storageUserToken) {
    rootStore.setDataItem(userTokenKey, storageUserToken);
    return storageUserToken;
  }

  return '';
}

export function setUserToken(token?: string | null) {
  const userTokenKey = key('user-token');
  rootStore.setDataItem(userTokenKey, token);
  write(userTokenKey, token);
}

/** hasHydraSession 读取旧 Web 约定的 Hydra 会话提示标记，保持跨版本兼容。 */
export function hasHydraSession() {
  return read(HYDRA_SESSION_KEY) === '1';
}

/** setHydraSession 写入或移除旧 Web 使用的 Hydra 会话提示标记。 */
export function setHydraSession(active: boolean) {
  write(HYDRA_SESSION_KEY, active ? '1' : undefined);
}

export function getTenantID() {
  const tenantIDKey = key('tenant-id');

  const tenantIDValue = rootStore.getDataItem(tenantIDKey);
  if (tenantIDValue) {
    return tenantIDValue;
  }

  const storageTenantID = read(tenantIDKey);
  if (storageTenantID) {
    rootStore.setDataItem(tenantIDKey, storageTenantID);
    return storageTenantID;
  }

  return null;
}

export function setTenantID(tenantID?: string | null) {
  const tenantIDKey = key('tenant-id');
  rootStore.setDataItem(tenantIDKey, tenantID);
  write(tenantIDKey, tenantID);
}

/** getTenant 按租户读取单位信息，优先使用内存缓存，并用本地存储完成首次回填。 */
export function getTenant<T extends object = Record<string, unknown>>(
  tenantID = getTenantID(),
): T | undefined {
  if (!tenantID) {
    return undefined;
  }

  const tenantKey = tenantScopedKey('tenant', tenantID);

  const tenantValue = rootStore.getDataItem(tenantKey);
  // 单位对象可能暂时只有少量字段，缓存命中只按空值判断，不能用空对象判定代替。
  if (!isVacuous(tenantValue)) {
    return tenantValue as T;
  }

  const storageTenant = readJSON<T>(tenantKey);
  if (!isVacuous(storageTenant)) {
    rootStore.setDataItem(tenantKey, storageTenant);
    return storageTenant;
  }

  return undefined;
}

/** setTenant 按租户同步单位信息到内存和本地存储；空值表示清理目标租户。 */
export function setTenant<T extends object>(
  tenant?: T | null,
  tenantID = getTenantID(),
): void {
  if (!tenantID) {
    return;
  }

  const tenantKey = tenantScopedKey('tenant', tenantID);
  rootStore.setDataItem(tenantKey, tenant);
  writeJSON(tenantKey, tenant);
}

/** getRealm 仅从与当前 tenantID 匹配的租户读取渠道，避免残留会话覆盖站点 Realm。 */
export function getRealm() {
  const tenantID = getTenantID().trim();
  if (!tenantID) return '';
  const tenant = getTenant<{ id?: string | number; realm?: string }>();
  if (String(tenant?.id ?? '') !== tenantID) return '';
  return tenant?.realm?.trim() ?? '';
}

export function getStaffToken(tenantID = getTenantID()) {
  const staffTokenKey = tenantScopedKey('staff-token', tenantID);

  const staffTokenValue = rootStore.getDataItem(staffTokenKey);
  if (staffTokenValue) {
    return staffTokenValue;
  }

  const storageStaffToken = read(staffTokenKey);
  if (storageStaffToken) {
    rootStore.setDataItem(staffTokenKey, storageStaffToken);
    return storageStaffToken;
  }

  return null;
}

export function setStaffToken(token?: string | null, tenantID = getTenantID()) {
  const staffTokenKey = tenantScopedKey('staff-token', tenantID);
  rootStore.setDataItem(staffTokenKey, token);
  write(staffTokenKey, token);
}

export function getUser<T = unknown>() {
  return readJSON<T>(key('user'));
}

export function setUser<T>(user?: T | null) {
  writeJSON(key('user'), user);
}

/** setStaff 按租户同步员工信息到内存和本地存储，空值仅清理目标租户。 */
export function setStaff<T>(staff?: T | null, tenantID = getTenantID()) {
  if (!tenantID) {
    return undefined;
  }
  const staffKey = tenantScopedKey('staff', tenantID);
  rootStore.setDataItem(staffKey, staff);
  writeJSON(staffKey, staff);
}

/** getStaff 按租户读取员工信息，并在内存未命中时从本地存储回填。 */
export function getStaff<T = unknown>(tenantID = getTenantID()) {
  if (!tenantID) {
    return undefined;
  }

  const staffKey = tenantScopedKey('staff', tenantID);

  const staffValue = rootStore.getDataItem(staffKey);
  if (!isVacuous(staffValue)) {
    return staffValue;
  }

  const storageStaff = readJSON<T>(staffKey);
  if (!isVacuous(storageStaff)) {
    rootStore.setDataItem(staffKey, storageStaff);
    return storageStaff;
  }

  return undefined;
}

export function getTenantStaffs<T = unknown>() {
  return readJSON<T[]>(key('tenant-staffs')) ?? [];
}

export function setTenantStaffs<T>(staffs?: T[] | null) {
  writeJSON(key('tenant-staffs'), staffs ?? []);
}

/** clearTenantAuthSession 仅清理目标租户员工态，平台用户和其他租户缓存保持不变。 */
export function clearTenantAuthSession(tenantID = getTenantID()) {
  setStaffToken(undefined, tenantID);
  setStaff(undefined, tenantID);
}

/** clearAuthSession 清理平台用户及当前租户员工态，避免失效凭证继续参与认证判断。 */
export function clearAuthSession() {
  const tenantID = getTenantID();
  // 员工态和单位信息都按租户隔离，必须保留当前 ID 完成清理后再移除 tenantID。
  clearTenantAuthSession(tenantID);
  setTenant(undefined, tenantID);
  // 旧版客户端仍可能留下全局单位键，完整退出时同步清理，避免残留会话继续被旧版本读取。
  write(key('tenant'));
  setUserToken();
  setTenant();
  setTenantID();
  setUser();
  setTenantStaffs();
}
